// src/modules/marketplace/pages/KrakenAnalyticsPage.tsx

import { useEffect, useMemo, useState } from "react"
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { SkeletonLoading, SnackbarAlert } from "@core/components"
import {
  CitiesBarChart,
  CitiesTimeseriesChart,
  OverviewChart,
} from "../components"
import {
  useKrakenCitiesQuery,
  useKrakenCitiesTimeseriesQuery,
  useKrakenOverviewQuery,
} from "../store"
import { Kraken } from "../interfaces"

function SectionPaper({
  title,
  hint,
  action,
  children,
}: {
  title: string
  hint?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: hint ? 1 : 2 }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "info.main",
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "info.main",
            opacity: 0.7,
          }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          {hint}
        </Typography>
      )}
      {children}
    </Paper>
  )
}

export function KrakenAnalyticsPage() {
  const [period, setPeriod] = useState<Kraken.Period>("week")
  const [selectedCity, setSelectedCity] = useState<string>("")

  const cities = useKrakenCitiesQuery({ queryParams: { months: 3 } })
  const overview = useKrakenOverviewQuery({ queryParams: { period } })
  const timeseries = useKrakenCitiesTimeseriesQuery({ queryParams: { period } })

  const cityNames = useMemo(
    () => timeseries.data?.cities.map((c) => c.city) ?? [],
    [timeseries.data],
  )

  // Default to the first city once data arrives / keep selection valid.
  useEffect(() => {
    if (!cityNames.length) return
    if (!cityNames.includes(selectedCity)) {
      setSelectedCity(cityNames[0])
    }
  }, [cityNames, selectedCity])

  const isError = cities.isError || overview.isError || timeseries.isError

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <SnackbarAlert
        isOpen={isError}
        variant="error"
        message="Ошибка загрузки аналитики кладов"
      />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1">
          Аналитика кладов
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Загруженные и проданные клады по основным городам
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Endpoint 1 — cities summary (fixed 3-month window) */}
        <SectionPaper
          title="Сводка по городам (3 мес.)"
          hint="Бары — загружено и продано (kraken/liga/прочие). Линия — средняя цена (₽)."
        >
          <SkeletonLoading
            isLoading={cities.isLoading}
            variant="rectangular"
            height={360}
          >
            {cities.data ? (
              <CitiesBarChart cities={cities.data.cities} />
            ) : (
              <></>
            )}
          </SkeletonLoading>
        </SectionPaper>

        {/* Endpoint 2 — overall time series */}
        <SectionPaper
          title="Динамика (все города)"
          hint="Бары — загружено и продано (kraken/liga/прочие). Линия — конверсия (%)."
          action={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={period}
              onChange={(_, v: Kraken.Period | null) => v && setPeriod(v)}
            >
              <ToggleButton value="week">Неделя (3 мес.)</ToggleButton>
              <ToggleButton value="month">Месяц (6 мес.)</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <SkeletonLoading
            isLoading={overview.isLoading}
            variant="rectangular"
            height={320}
          >
            {overview.data ? (
              <OverviewChart points={overview.data.points} period={period} />
            ) : (
              <></>
            )}
          </SkeletonLoading>
        </SectionPaper>

        {/* Endpoint 3 — per-city time series */}
        <SectionPaper
          title="Динамика по городам"
          hint="Бары — загружено и продано (kraken/liga/прочие). Линия — конверсия (%)."
          action={
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="kraken-city-label">Город</InputLabel>
              <Select
                labelId="kraken-city-label"
                label="Город"
                value={cityNames.includes(selectedCity) ? selectedCity : ""}
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                {cityNames.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        >
          <SkeletonLoading
            isLoading={timeseries.isLoading}
            variant="rectangular"
            height={360}
          >
            {timeseries.data ? (
              <CitiesTimeseriesChart
                cities={timeseries.data.cities}
                period={period}
                selectedCity={selectedCity}
              />
            ) : (
              <></>
            )}
          </SkeletonLoading>
        </SectionPaper>
      </Stack>
    </Container>
  )
}
